"use client";

import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from "react";
import type { EditorWorkspaceHandle } from "../editor-workspace";
import { classNames } from "../ui/class-names";
import {
  REPO_MARKDOWN_IMPORT_COOLDOWN_SECONDS,
  clearRepoMarkdownImportCooldown,
  readRepoMarkdownImportCooldownRemainingSeconds,
  startRepoMarkdownImportCooldown,
} from "../utils/repo-markdown-import-cooldown";
import { takeRepoMarkdownImportDraft } from "../utils/repo-markdown-import-draft";

type ImportStatus =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "cooldown"; remainingSeconds: number; successTitle?: string }
  | { kind: "error"; message: string };

export function RepoMarkdownImportToolbar({
  workspaceRef,
  workspaceReady,
  className,
  inputId = "repo-markdown-url",
  repoUrl: controlledRepoUrl,
  onRepoUrlChange,
  consumeDraft = false,
}: Readonly<{
  workspaceRef: RefObject<EditorWorkspaceHandle | null>;
  workspaceReady: boolean;
  className?: string;
  inputId?: string;
  repoUrl?: string;
  onRepoUrlChange?: (nextUrl: string) => void;
  consumeDraft?: boolean;
}>) {
  const importInFlightRef = useRef(false);
  const [uncontrolledRepoUrl, setUncontrolledRepoUrl] = useState("");
  const [status, setStatus] = useState<ImportStatus>({ kind: "idle" });
  const isControlled = controlledRepoUrl !== undefined;
  const repoUrl = isControlled ? controlledRepoUrl : uncontrolledRepoUrl;

  function updateRepoUrl(nextUrl: string) {
    if (isControlled) {
      onRepoUrlChange?.(nextUrl);
      return;
    }
    setUncontrolledRepoUrl(nextUrl);
  }

  function beginImportCooldown(
    durationSeconds: number,
    successTitle?: string,
  ) {
    const remainingSeconds = startRepoMarkdownImportCooldown(durationSeconds);
    setStatus({
      kind: "cooldown",
      remainingSeconds,
      ...(successTitle ? { successTitle } : {}),
    });
  }

  useEffect(() => {
    if (consumeDraft) {
      const draft = takeRepoMarkdownImportDraft();
      if (draft) {
        /* eslint-disable react-hooks/set-state-in-effect */
        setUncontrolledRepoUrl(draft);
        /* eslint-enable react-hooks/set-state-in-effect */
      }
    }

    const remainingSeconds = readRepoMarkdownImportCooldownRemainingSeconds();

    if (remainingSeconds !== null) {
      setStatus({ kind: "cooldown", remainingSeconds });
    }
  }, [consumeDraft]);

  useEffect(() => {
    if (status.kind !== "cooldown") {
      return;
    }

    if (status.remainingSeconds <= 0) {
      clearRepoMarkdownImportCooldown();
      /* eslint-disable react-hooks/set-state-in-effect */
      setStatus({ kind: "idle" });
      /* eslint-enable react-hooks/set-state-in-effect */
      return;
    }

    const successTitle = status.successTitle;
    const timeoutId = window.setTimeout(() => {
      const remainingSeconds =
        readRepoMarkdownImportCooldownRemainingSeconds();

      if (remainingSeconds === null) {
        clearRepoMarkdownImportCooldown();
        setStatus({ kind: "idle" });
        return;
      }

      setStatus({
        kind: "cooldown",
        remainingSeconds,
        ...(successTitle ? { successTitle } : {}),
      });
    }, 1_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status]);

  const isLoading = status.kind === "loading";
  const isCooldown = status.kind === "cooldown";
  const canSubmit =
    workspaceReady &&
    !isLoading &&
    !isCooldown &&
    repoUrl.trim().length > 0;

  async function importRepo(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const url = repoUrl.trim();
    if (
      !url ||
      !workspaceReady ||
      isLoading ||
      isCooldown ||
      importInFlightRef.current
    ) {
      return;
    }

    importInFlightRef.current = true;
    setStatus({ kind: "loading" });

    try {
      const response = await fetch("/api/repo-markdown", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ url }),
      });

      const payload = (await response.json().catch(() => null)) as
        | {
            markdown?: string;
            title?: string;
            filename?: string;
            error?: { message?: string; code?: string };
          }
        | null;

      if (response.status === 429) {
        const retryAfterHeader = Number(response.headers.get("Retry-After"));
        const retryAfterSeconds = Number.isFinite(retryAfterHeader)
          ? retryAfterHeader
          : REPO_MARKDOWN_IMPORT_COOLDOWN_SECONDS;
        beginImportCooldown(retryAfterSeconds);
        return;
      }

      if (!response.ok) {
        setStatus({
          kind: "error",
          message:
            payload?.error?.message ??
            "Could not import Markdown from that repository URL.",
        });
        return;
      }

      const markdown = payload?.markdown;
      const filename = payload?.filename;
      const title = payload?.title ?? "README";

      if (typeof markdown !== "string" || typeof filename !== "string") {
        setStatus({
          kind: "error",
          message: "Could not import Markdown from that repository URL.",
        });
        return;
      }

      const replaced = workspaceRef.current?.replaceDocument({
        markdown,
        filename,
      });

      if (replaced === false) {
        setStatus({ kind: "idle" });
        return;
      }

      if (!replaced) {
        setStatus({
          kind: "error",
          message: "The editor is still loading. Try again in a moment.",
        });
        return;
      }

      beginImportCooldown(REPO_MARKDOWN_IMPORT_COOLDOWN_SECONDS, title);
    } catch {
      setStatus({
        kind: "error",
        message:
          "Could not reach the import service. Check your connection and try again.",
      });
    } finally {
      importInFlightRef.current = false;
    }
  }

  const showStatus =
    status.kind === "loading" ||
    status.kind === "error" ||
    status.kind === "cooldown" ||
    !workspaceReady;

  const statusMessage =
    status.kind === "loading"
      ? "Fetching Markdown from the repository…"
      : status.kind === "error"
        ? status.message
        : status.kind === "cooldown" && status.successTitle
          ? `Imported “${status.successTitle}” into the editor.`
          : !workspaceReady
            ? "Loading editor…"
            : null;

  const buttonLabel = isLoading
    ? "Importing…"
    : isCooldown
      ? `Wait ${status.remainingSeconds}s`
      : "Import";

  return (
    <form
      className={classNames(
        "shrink-0 border-b border-zinc-200 bg-white px-3 py-2.5",
        className,
      )}
      onSubmit={importRepo}
    >
      <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
        <span
          className="hidden text-sm font-medium text-zinc-700 sm:col-start-1 sm:row-start-1 sm:inline"
          aria-hidden="true"
        >
          Repo or Markdown URL
        </span>
        <label htmlFor={inputId} className="sr-only">
          Import a public repository or Markdown file URL
        </label>
        <div className="min-w-0 sm:col-start-2 sm:row-start-1">
          <div
            className={classNames(
              "flex h-10 w-full items-stretch overflow-hidden rounded-md border border-zinc-300 bg-white shadow-sm",
              "focus-within:border-brand focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-hover focus-within:ring-2 focus-within:ring-brand/20",
            )}
          >
            <input
              id={inputId}
              type="url"
              name="repoUrl"
              value={repoUrl}
              onChange={(event) => {
                updateRepoUrl(event.target.value);
                if (status.kind === "error") {
                  setStatus({ kind: "idle" });
                }
              }}
              placeholder="https://github.com/owner/repo or …/README.md"
              title="Paste a public repository or Markdown file URL"
              autoComplete="off"
              spellCheck={false}
              disabled={isLoading || isCooldown}
              className="min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-zinc-950 outline-none placeholder:text-zinc-400 disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={!canSubmit}
              title={
                isCooldown
                  ? `Wait ${status.remainingSeconds}s before importing again`
                  : undefined
              }
              className={classNames(
                "inline-flex shrink-0 items-center justify-center border-l px-4 text-sm font-semibold transition",
                "border-brand bg-brand text-white hover:bg-brand-hover",
                "disabled:cursor-not-allowed disabled:opacity-45",
              )}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
        <div
          className="min-w-0 sm:col-start-2 sm:row-start-2"
          aria-live="polite"
        >
          {showStatus && statusMessage ? (
            <p
              className={classNames(
                "text-xs leading-relaxed",
                status.kind === "error"
                  ? "text-red-700"
                  : status.kind === "cooldown" && status.successTitle
                    ? "text-emerald-700"
                    : "text-zinc-500",
              )}
            >
              {statusMessage}
            </p>
          ) : null}
        </div>
      </div>
    </form>
  );
}
